import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Plus,
  Sparkle,
  File,
  Download,
  Phone,
  Envelope,
  Buildings,
  CalendarBlank,
  CurrencyDollar,
  ChatText,
  Clock,
  User,
  X,
  Upload
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  lead: 'bg-gray-100 text-gray-700',
  qualified: 'bg-blue-100 text-blue-700',
  demo_scheduled: 'bg-purple-100 text-purple-700',
  proposal: 'bg-orange-100 text-orange-700',
  closed_won: 'bg-green-100 text-green-700',
  closed_lost: 'bg-red-100 text-red-700'
};

const statusLabels = {
  lead: 'Lead',
  qualified: 'Qualified',
  demo_scheduled: 'Demo Scheduled',
  proposal: 'Proposal',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost'
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showResearchModal, setShowResearchModal] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const { data } = await axios.get(`${API}/customers/${id}`, { withCredentials: true });
      setCustomer(data);
      setEditData(data);
    } catch (e) {
      console.error('Failed to fetch customer:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/customers/${id}`, editData, { withCredentials: true });
      setEditMode(false);
      fetchCustomer();
    } catch (e) {
      console.error('Failed to update:', e);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`, { withCredentials: true });
      navigate('/admin');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddNote = async (content, noteType) => {
    try {
      await axios.post(`${API}/customers/${id}/notes`, { content, note_type: noteType }, { withCredentials: true });
      fetchCustomer();
      setShowNoteModal(false);
    } catch (e) {
      console.error('Failed to add note:', e);
    }
  };

  const handleAddInteraction = async (type, summary, details) => {
    try {
      await axios.post(`${API}/customers/${id}/interactions`, { 
        interaction_type: type, 
        summary, 
        details 
      }, { withCredentials: true });
      fetchCustomer();
      setShowInteractionModal(false);
    } catch (e) {
      console.error('Failed to add interaction:', e);
    }
  };

  const handleAIResearch = async (companyName, context) => {
    setResearchLoading(true);
    try {
      await axios.post(`${API}/customers/${id}/ai-research`, {
        company_name: companyName,
        additional_context: context
      }, { withCredentials: true });
      fetchCustomer();
      setShowResearchModal(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'AI research failed');
    } finally {
      setResearchLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/customers/${id}/files`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchCustomer();
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, { withCredentials: true });
      fetchCustomer();
    } catch (e) {
      console.error('Failed to delete file:', e);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-[#555555]">Loading...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#555555] mb-4">Customer not found</p>
          <button onClick={() => navigate('/admin')} className="text-[#002FA7] hover:underline">← Back to dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="customer-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-[#555555] hover:text-[#111111]">
            <ArrowLeft size={20} />
            Back to Customers
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowResearchModal(true)}
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 hover:bg-purple-700 transition-colors"
              data-testid="ai-research-btn"
            >
              <Sparkle size={18} />
              AI Research
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
                data-testid="delete-customer-btn"
              >
                <Trash size={18} />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Customer Info */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E5E5] p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="font-heading font-bold text-2xl text-[#111111]" data-testid="customer-name">{customer.name}</h1>
                  <p className="text-[#555555]">{customer.company || 'No company'}</p>
                </div>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="text-[#555555] hover:text-[#002FA7]"
                >
                  <PencilSimple size={20} />
                </button>
              </div>

              <span className={`inline-block px-3 py-1 text-sm font-medium ${statusColors[customer.status]}`}>
                {statusLabels[customer.status]}
              </span>

              {editMode ? (
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-xs text-[#555555] mb-1">Status</label>
                    <select
                      value={editData.status}
                      onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                      className="w-full border border-[#E5E5E5] px-3 py-2 text-sm"
                    >
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#555555] mb-1">Product Interest</label>
                    <select
                      value={editData.product_interest || ''}
                      onChange={(e) => setEditData({ ...editData, product_interest: e.target.value })}
                      className="w-full border border-[#E5E5E5] px-3 py-2 text-sm"
                    >
                      <option value="">None</option>
                      <option value="fractionalrisk.ai">fractionalrisk.ai</option>
                      <option value="risksurvey.ai">risksurvey.ai</option>
                      <option value="riskcoach.ai">riskcoach.ai</option>
                      <option value="Other Services">Other Services</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#555555] mb-1">Deal Value</label>
                    <input
                      type="number"
                      value={editData.deal_value || ''}
                      onChange={(e) => setEditData({ ...editData, deal_value: e.target.value ? parseFloat(e.target.value) : null })}
                      className="w-full border border-[#E5E5E5] px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#555555] mb-1">Expected Close Date</label>
                    <input
                      type="date"
                      value={editData.expected_close_date || ''}
                      onChange={(e) => setEditData({ ...editData, expected_close_date: e.target.value })}
                      className="w-full border border-[#E5E5E5] px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditMode(false)} className="flex-1 border border-[#E5E5E5] py-2 text-sm">Cancel</button>
                    <button onClick={handleUpdate} className="flex-1 bg-[#002FA7] text-white py-2 text-sm">Save</button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3 text-sm">
                    <Envelope size={18} className="text-[#555555]" />
                    <a href={`mailto:${customer.email}`} className="text-[#002FA7] hover:underline">{customer.email}</a>
                  </div>
                  {customer.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone size={18} className="text-[#555555]" />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm">
                    <Buildings size={18} className="text-[#555555]" />
                    <span>{customer.company || 'No company'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CurrencyDollar size={18} className="text-[#555555]" />
                    <span>Deal Value: {formatCurrency(customer.deal_value)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CalendarBlank size={18} className="text-[#555555]" />
                    <span>Expected Close: {formatDate(customer.expected_close_date)}</span>
                  </div>
                  {customer.product_interest && (
                    <div className="pt-2 border-t border-[#E5E5E5]">
                      <p className="text-xs text-[#555555] mb-1">Product Interest</p>
                      <p className="text-sm font-medium">{customer.product_interest}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Files Section */}
            <div className="bg-white border border-[#E5E5E5] p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold">Files</h2>
                <label className="cursor-pointer text-[#002FA7] hover:underline text-sm flex items-center gap-1">
                  <Upload size={16} />
                  Upload
                  <input type="file" className="hidden" onChange={handleFileUpload} />
                </label>
              </div>
              
              {customer.files?.length > 0 ? (
                <div className="space-y-2">
                  {customer.files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-[#F3F3F3]">
                      <div className="flex items-center gap-2">
                        <File size={18} className="text-[#555555]" />
                        <span className="text-sm truncate max-w-[150px]">{file.filename}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`${API}/files/${file.id}/download`}
                          className="text-[#002FA7] hover:text-[#001D66]"
                        >
                          <Download size={16} />
                        </a>
                        <button onClick={() => handleDeleteFile(file.id)} className="text-red-500 hover:text-red-700">
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#555555]">No files uploaded</p>
              )}
            </div>
          </div>

          {/* Right Column - Activity */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="flex border-b border-[#E5E5E5] mb-6">
              {['activity', 'notes', 'research'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === tab ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-[#555555] hover:text-[#111111]'}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold">Interactions</h2>
                  <button
                    onClick={() => setShowInteractionModal(true)}
                    className="flex items-center gap-1 text-sm text-[#002FA7] hover:underline"
                  >
                    <Plus size={16} />
                    Log Interaction
                  </button>
                </div>

                {customer.interactions?.length > 0 ? (
                  <div className="space-y-4">
                    {customer.interactions.map((interaction) => (
                      <div key={interaction.id} className="bg-white border border-[#E5E5E5] p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-[#F3F3F3] text-xs font-medium">{interaction.interaction_type}</span>
                            <span className="text-xs text-[#555555]">{formatDate(interaction.created_at)}</span>
                          </div>
                          {interaction.created_by_name && (
                            <span className="text-xs text-[#555555]">by {interaction.created_by_name}</span>
                          )}
                        </div>
                        <p className="font-medium text-sm">{interaction.summary}</p>
                        {interaction.details && <p className="text-sm text-[#555555] mt-1">{interaction.details}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#555555] text-sm">No interactions logged yet</p>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold">Notes</h2>
                  <button
                    onClick={() => setShowNoteModal(true)}
                    className="flex items-center gap-1 text-sm text-[#002FA7] hover:underline"
                  >
                    <Plus size={16} />
                    Add Note
                  </button>
                </div>

                {customer.notes?.length > 0 ? (
                  <div className="space-y-4">
                    {customer.notes.map((note) => (
                      <div key={note.id} className="bg-white border border-[#E5E5E5] p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-2 py-0.5 bg-[#F3F3F3] text-xs font-medium">{note.note_type}</span>
                          <span className="text-xs text-[#555555]">{formatDate(note.created_at)} by {note.created_by_name}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#555555] text-sm">No notes yet</p>
                )}
              </div>
            )}

            {/* Research Tab */}
            {activeTab === 'research' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold">AI Research</h2>
                  <button
                    onClick={() => setShowResearchModal(true)}
                    className="flex items-center gap-1 text-sm bg-purple-600 text-white px-3 py-1.5 hover:bg-purple-700"
                  >
                    <Sparkle size={16} />
                    Generate Research
                  </button>
                </div>

                {customer.ai_research?.length > 0 ? (
                  <div className="space-y-4">
                    {customer.ai_research.map((research) => (
                      <div key={research.id} className="bg-white border border-[#E5E5E5] p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium">{research.company_name}</h3>
                          <span className="text-xs text-[#555555]">{formatDate(research.created_at)}</span>
                        </div>
                        <div className="prose prose-sm max-w-none text-[#555555] whitespace-pre-wrap">
                          {research.research_content}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                    <Sparkle size={32} className="mx-auto text-purple-400 mb-3" />
                    <p className="text-[#555555] mb-3">No AI research generated yet</p>
                    <button
                      onClick={() => setShowResearchModal(true)}
                      className="text-[#002FA7] hover:underline text-sm"
                    >
                      Generate sales intelligence for this customer
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Note Modal */}
      {showNoteModal && (
        <NoteModal onClose={() => setShowNoteModal(false)} onSave={handleAddNote} />
      )}

      {/* Interaction Modal */}
      {showInteractionModal && (
        <InteractionModal onClose={() => setShowInteractionModal(false)} onSave={handleAddInteraction} />
      )}

      {/* Research Modal */}
      {showResearchModal && (
        <ResearchModal 
          onClose={() => setShowResearchModal(false)} 
          onSave={handleAIResearch} 
          loading={researchLoading}
          defaultCompany={customer.company}
        />
      )}
    </div>
  );
};

const NoteModal = ({ onClose, onSave }) => {
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState('general');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (content.trim()) {
      onSave(content, noteType);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md">
        <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="font-heading font-semibold">Add Note</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select value={noteType} onChange={(e) => setNoteType(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2">
              <option value="general">General</option>
              <option value="call_notes">Call Notes</option>
              <option value="meeting_notes">Meeting Notes</option>
              <option value="email_summary">Email Summary</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Note</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full border border-[#E5E5E5] px-3 py-2 h-32 resize-none"
              placeholder="Enter your note..."
              required
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#E5E5E5] py-2">Cancel</button>
            <button type="submit" className="flex-1 bg-[#002FA7] text-white py-2">Save Note</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InteractionModal = ({ onClose, onSave }) => {
  const [type, setType] = useState('call');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (summary.trim()) {
      onSave(type, summary, details);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md">
        <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="font-heading font-semibold">Log Interaction</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full border border-[#E5E5E5] px-3 py-2">
              <option value="call">Phone Call</option>
              <option value="email">Email</option>
              <option value="meeting">Meeting</option>
              <option value="demo">Demo</option>
              <option value="proposal">Proposal Sent</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Summary</label>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full border border-[#E5E5E5] px-3 py-2"
              placeholder="Brief summary..."
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Details (optional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="w-full border border-[#E5E5E5] px-3 py-2 h-24 resize-none"
              placeholder="Additional details..."
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#E5E5E5] py-2">Cancel</button>
            <button type="submit" className="flex-1 bg-[#002FA7] text-white py-2">Log Interaction</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ResearchModal = ({ onClose, onSave, loading, defaultCompany }) => {
  const [companyName, setCompanyName] = useState(defaultCompany || '');
  const [context, setContext] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (companyName.trim()) {
      onSave(companyName, context);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ai-research-modal">
      <div className="bg-white w-full max-w-md">
        <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle size={20} className="text-purple-600" />
            <h2 className="font-heading font-semibold">AI Sales Intelligence</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-[#555555]">Generate AI-powered research to prepare for your sales call.</p>
          <div>
            <label className="block text-sm font-medium mb-1">Company Name *</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full border border-[#E5E5E5] px-3 py-2"
              placeholder="e.g., Acme Insurance Corp"
              required
              data-testid="research-company-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Additional Context (optional)</label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="w-full border border-[#E5E5E5] px-3 py-2 h-24 resize-none"
              placeholder="Any specific topics to research, recent news to consider, or talking points needed..."
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#E5E5E5] py-2" disabled={loading}>Cancel</button>
            <button 
              type="submit" 
              className="flex-1 bg-purple-600 text-white py-2 flex items-center justify-center gap-2 hover:bg-purple-700 disabled:opacity-50"
              disabled={loading}
              data-testid="research-submit-btn"
            >
              {loading ? 'Researching...' : <><Sparkle size={18} /> Generate</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerDetail;
